import React, { useState } from 'react';
import { Check, Zap, Crown, MessageSquare, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PricingPage = () => {
  const { t } = useLanguage();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const subscriptionPlans = [
    {
      id: 'agents',
      name: t('agents'),
      description: t('agentsDescription'),
      price: {
        monthly: 299,
        yearly: 2990
      },
      features: [
        t('customAIAgent'),
        t('websiteSocialAnalysis'),
        t('productCatalogSync'),
        t('whatsappIntegration'),
        t('instagramFacebookConnect'),
        t('basicAnalytics'),
        t('emailSupport'),
        t('unlimitedProfiles')
      ],
      icon: Zap,
      color: 'blue',
      popular: false
    },
    {
      id: 'full',
      name: t('full'),
      description: t('fullDescription'),
      price: {
        monthly: 699,
        yearly: 6990
      },
      features: [
        t('everythingInAgents'),
        t('aiTrafficManager'),
        t('googleAdsAutomation'),
        t('metaAdsManagement'),
        t('campaignOptimization'),
        t('advancedAnalytics'),
        t('leadQualificationSystem'),
        t('prioritySupport'),
        t('unlimitedProfiles'),
        t('customIntegrations')
      ],
      icon: Crown,
      color: 'primary',
      popular: true
    }
  ];

  const messagingPackages = [
    {
      range: t('upTo1000'),
      price: 1500,
      description: t('perfectSmallBusiness')
    },
    {
      range: '1.001 - 2.000',
      price: 2850,
      description: t('growingBusinesses')
    },
    {
      range: '2.001 - 3.000',
      price: 4050,
      description: t('establishedCompanies')
    },
    {
      range: '3.001 - 4.000',
      price: 5100,
      description: t('highVolumeOperations')
    },
    {
      range: '4.001 - 5.000',
      price: 6000,
      description: t('enterpriseLevel')
    },
    {
      range: t('above5000'),
      price: 'custom',
      description: t('customPricing'),
      isCustom: true
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        button: 'bg-blue-600 hover:bg-blue-700',
        text: 'text-blue-600'
      },
      primary: {
        bg: 'bg-primary-50',
        border: 'border-primary-200',
        button: 'bg-primary-600 hover:bg-primary-700',
        text: 'text-primary-600'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-secondary-900 mb-4 font-display">
            {t('simpleTransparentPricing')}
          </h1>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            {t('choosePerfectPlan')}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-secondary-100 p-1 rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {t('monthly')}
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {t('yearly')}
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                {t('save17')}
              </span>
            </button>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {subscriptionPlans.map((plan) => {
            const colors = getColorClasses(plan.color);
            const Icon = plan.icon;
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 ${colors.border} ${colors.bg} p-8 ${
                  plan.popular ? 'ring-2 ring-primary-500 ring-offset-2' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      {t('mostPopular')}
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${colors.bg} rounded-full mb-4`}>
                    <Icon className={`w-8 h-8 ${colors.text}`} />
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-2">{plan.name}</h3>
                  <p className="text-secondary-600 mb-4">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-secondary-900">
                      R${plan.price[billingCycle].toLocaleString()}
                    </span>
                    <span className="text-secondary-600 ml-2">
                      /{billingCycle === 'monthly' ? t('month') : t('year')}
                    </span>
                  </div>
                  
                  <button className={`w-full text-white px-6 py-3 rounded-lg font-medium transition-colors ${colors.button}`}>
                    {t('getStarted')}
                  </button>
                </div>
                
                <div className="space-y-4">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-secondary-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Messaging Packages */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4 font-display">
              {t('messageVolumePricing')}
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              {t('payOnlyMessages')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {messagingPackages.map((pkg, index) => (
              <div
                key={index}
                className="bg-white border border-secondary-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="text-center">
                  <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-6 h-6 text-primary-600" />
                  </div>
                  
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    {pkg.range} {t('messages')}
                  </h3>
                  
                  <div className="mb-4">
                    {pkg.isCustom ? (
                      <span className="text-3xl font-bold text-secondary-900">
                        {t('customQuote')}
                      </span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-secondary-900">
                          R${pkg.price.toLocaleString()}
                        </span>
                        <span className="text-secondary-600 ml-1">
                          /{t('month')}
                        </span>
                      </>
                    )}
                  </div>
                  
                  <p className="text-sm text-secondary-600 mb-6">
                    {pkg.description}
                  </p>
                  
                  <button className="w-full bg-secondary-100 hover:bg-secondary-200 text-secondary-900 px-4 py-2 rounded-lg font-medium transition-colors">
                    {pkg.isCustom ? t('contactUs') : t('selectPackage')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-secondary-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-secondary-900 mb-8 text-center font-display">
            {t('frequentlyAskedQuestions')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-secondary-900 mb-2">
                {t('whatsIncludedAgents')}
              </h3>
              <p className="text-secondary-600 text-sm">
                {t('agentsIncludesDesc')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-secondary-900 mb-2">
                {t('howTrafficManagerWorks')}
              </h3>
              <p className="text-secondary-600 text-sm">
                {t('trafficManagerDesc')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-secondary-900 mb-2">
                {t('canChangeMessagePackage')}
              </h3>
              <p className="text-secondary-600 text-sm">
                {t('changePackageDesc')}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-secondary-900 mb-2">
                {t('isThereFreeTrial')}
              </h3>
              <p className="text-secondary-600 text-sm">
                {t('freeTrialDesc')}
              </p>
            </div>
          </div>
        </div>

        {/* Enterprise Section */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white text-center mb-16">
          <h2 className="text-2xl font-bold mb-4 font-display">
            {t('enterpriseSolution')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('enterpriseDescription')}
          </p>
          <button className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-3 rounded-lg font-medium transition-colors">
            {t('contactUs')}
          </button>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-secondary-900 mb-4 font-display">
            {t('readyToAutomateBusiness')}
          </h2>
          <p className="text-lg text-secondary-600 mb-8 max-w-2xl mx-auto">
            {t('joinThousandsBusinesses')}
          </p>
          <button className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-medium text-lg transition-colors">
            {t('startFreeTrialBtn')}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;
